import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { TranslateService } from "@ngx-translate/core";
import { Log } from "ng2-logger";

import * as FileSaver from "file-saver";
import * as _ from "lodash";

import { AuditsTableComponent } from '@app/roles/admin/pages/audits-management/components/audits-table/audits-table.component';
import { Audit } from '@models/audit.model';
import { AuditsService } from "@services/audits.service";

import { TableConfig } from '@app/shared/util/table-config.utils';
import { ObservableService } from '@app/core/services/observable.service';
import { Subscription } from 'rxjs';
import { ToastsManager } from "@app/shared/util/toastr.service";

const entities = [
    {id: 1, name: 'Tools', value: 'tools'},
    {id: 2, name: 'Users', value: 'users'},
    {id: 3, name: 'Roles', value: 'roles'},
    {id: 4, name: 'Plants', value: 'plants'},
    {id: 5, name: 'Lines', value: 'lines'},
    {id: 6, name: 'Equipment', value: 'equipments'},
    {id: 7, name: 'Part Numbers', value: 'partNumbers'},
    {id: 8, name: 'Insertion Cycles', value: 'insertionCycles'},
    {id: 9, name: 'Lifts', value: 'lifts'},
    {id: 10, name: 'Trays', value: 'trays'},
    {id: 11, name: 'Slots', value: 'slots'},
    {id: 12, name: 'Recurrences', value: 'recurrences'},
    {id: 13, name: 'Tasks', value: 'operationTasks'},
    {id: 14, name: 'Plans', value: 'maintenancePlans'},
    {id: 15, name: 'Operations', value: 'operations'},
    {id: 16, name: 'Lift Batches', value: 'batches'}
];

const operations = [
    {id: 1, name: 'All', value: 'all'},
    {id: 2, name: 'Added', value: 'added'},
    {id: 3, name: 'Modified', value: 'modified'},
    {id: 4, name: 'Deleted', value: 'deleted'}
];

@Component({
    selector: 'app-audits-management',
    templateUrl: './audits.component.html',
    styleUrls: ['./audits.component.scss']
})
export class AuditsComponent implements OnInit, OnDestroy {
    _logger = Log.create(AuditsComponent.name);

    @ViewChild(AuditsTableComponent, {static: true}) auditsTable: AuditsTableComponent;

    audits: Audit[] = [];

    tableConfig = new TableConfig();

    formFilters: any = [];
    tableFilters: any = [];

    isLoadingAudits = false;

    entities = entities;
    operations = operations;
    selectedEntity: any = entities[0];
    languageReloadSubscription: Subscription;

    constructor(private toastr: ToastsManager,
                private translateService: TranslateService,
                private auditService: AuditsService,
                observableService: ObservableService) {
        this.translateEntities();
        this.translateOperations();
        this.languageReloadSubscription = observableService.hasChanged$.subscribe(
            () => {
                this.getAudits();
            }
        )
    }

    ngOnInit() {
        this.getAudits({
            ...this.tableConfig,
            filters: [{field: 'entity', value: 'tools'}],
        } as TableConfig);
    }

    ngOnDestroy() {
        this.languageReloadSubscription.unsubscribe();
    }

    getAudits(tableConfig = this.tableConfig) {
        this.isLoadingAudits = true;
        this.audits = [];
        this.auditService.requestAudits(tableConfig).subscribe(data => {
                this.audits = data.audits;
                this.tableConfig.pager.count = data.count;
                this.isLoadingAudits = false;
            },
            (error: any) => {
                this.isLoadingAudits = false;
                this._logger.error('onRequestAudits:: API Returned error', error);
            });
    }

    downloadAudits(tableConfig = this.tableConfig) {
        this.auditService.downloadAudits(tableConfig).subscribe(
            (data: HttpResponse<any>) => {
                if (data.body != null) {
                    this._logger.info('onDownloadAudits:: API Returned data', data);
                    const blob = new Blob([data.body], {type: 'application/xsls'});
                    const filename = data.headers.get('x-filename');
                    FileSaver.saveAs(blob, filename);
                }
            },
            (error: any) => {
                this._logger.error('onDownloadAudits:: API Returned error', error);
            });
    }

    translateEntities() {
        this.translateService.stream('entities').subscribe(newEntities => {
            this.entities = this.entities.map(entity => {
                entity.name = newEntities[entity.value];
                return entity;
            });
            this._logger.info('translateEntities::', this.entities);
        });
    }

    translateOperations() {
        this.translateService.stream('admin.audits.table.filters.operation.options').subscribe(newOperations => {
            this.operations = this.operations.map(operation => {
                operation.name = newOperations[operation.value];
                return operation;
            });
            this._logger.info('translateOperations::', this.entities);
        });
    }

    onEntityChange(entity) {
        this.selectedEntity = entity;
        this.tableFilters = [];
        this.tableConfig = new TableConfig();
    }

    onFormChange(formFilters: any = []) {
        this.formFilters = formFilters;

        this.tableConfig.filters = _.union(this.formFilters, this.tableFilters);
        this.getAudits();
    }

    onFilterChange(tableConfig: TableConfig) {
        this.tableFilters = tableConfig.filters;
        tableConfig.filters = _.union(this.formFilters, this.tableFilters);
        this.getAudits(tableConfig);
    }

    onPageChange(tableConfig: TableConfig) {
        this.tableFilters = tableConfig.filters;
        tableConfig.filters = _.union(this.formFilters, this.tableFilters);
        this.getAudits(tableConfig);
    }

    onSortChange(tableConfig: TableConfig) {
        this.getAudits(tableConfig);
    }
}
